export function getStyles(element) {
  try {
    return window.getComputedStyle(element);
  } catch {
    return {};
  }
}

// Helper function to parse inline style string
function parseInlineStyle(styleStr) {
  const styleMap = {};
  if (!styleStr) return styleMap;

  styleStr.split(';').forEach((rule) => {
    const colonIndex = rule.indexOf(':');
    if (colonIndex > 0) {
      const property = rule.substring(0, colonIndex).trim();
      const value = rule.substring(colonIndex + 1).trim();
      if (property && value) {
        styleMap[property] = value;
      }
    }
  });

  return styleMap;
}

// Helper function to get element's transform matrix
export function getElementTransform(element) {
  const transform = getStyleValue(element, 'transform', '');
  if (!transform) return null;

  // Parse common transform functions
  const transforms = transform.match(/(\w+\([^)]+\))/g) || [];
  let matrix = [1, 0, 0, 1, 0, 0]; // Identity matrix

  transforms.forEach((t) => {
    const match = t.match(/(\w+)\(([^)]+)\)/);
    if (!match) return;

    const type = match[1];
    const values = match[2]
      .split(/[\s,]+/)
      .map(Number)
      .filter((n) => !isNaN(n));

    switch (type) {
      case 'translate':
        if (values.length >= 1) {
          const tx = values[0];
          const ty = values[1] || 0;
          matrix = multiplyMatrix(matrix, [1, 0, 0, 1, tx, ty]);
        }
        break;
      case 'scale':
        if (values.length >= 1) {
          const sx = values[0];
          const sy = values[1] || sx;
          matrix = multiplyMatrix(matrix, [sx, 0, 0, sy, 0, 0]);
        }
        break;
      case 'rotate':
        if (values.length >= 1) {
          const angle = (values[0] * Math.PI) / 180;
          const cos = Math.cos(angle);
          const sin = Math.sin(angle);
          matrix = multiplyMatrix(matrix, [cos, sin, -sin, cos, 0, 0]);
        }
        break;
      case 'matrix':
        if (values.length >= 6) {
          matrix = multiplyMatrix(matrix, values);
        }
        break;
    }
  });

  return matrix;
}

export function resolveURLValue(fill, defs = {}) {
  // Normalize fill value by removing quotes inside url()
  // Example: url("#gradient1") -> url(#gradient1)
  if ((fill && fill.includes('url("')) || fill.includes("url('")) {
    const normalizedFill = fill.replace(/url\(['"](.+?)['"]\)/g, 'url($1)');
    const gradients = defs?.gradients || {};
    const patterns = defs?.patterns || {};
    // Check if the normalized fill exists in gradients or patterns
    if (gradients[normalizedFill] || patterns[normalizedFill]) {
      return normalizedFill;
    }
  }
  return fill;
}

export function resolveFontFamily(element, _styles) {
  const value = getStyleValue(element, 'font-family', 'Arial');
  if (!value) return '';

  // Remove quotes and normalize font family
  const cleanValue = value.replace(/['"]/g, '').trim();

  // Extract only the first font family when multiple fonts are specified
  const firstFont = cleanValue.split(',')[0].trim();
  return firstFont;
}

export function resolveFontSize(element, _styles) {
  const value = getStyleValue(element, 'font-size', '16');
  return parseFloat(value) || 16;
}

// Enhanced style resolution function with priority handling
export function getStyleValue(element, property, fallback = null) {
  const styles = getStyles(element);

  // Convert CSS property to both camelCase and kebab-case for compatibility
  const camelCase = property.replace(/-([a-z])/g, (match, letter) => letter.toUpperCase());
  const kebabCase = property.replace(/([A-Z])/g, '-$1').toLowerCase();

  // Priority order: inline style > computed style > attribute > fallback
  let value = null;

  // 1. Check inline style (highest priority)
  const inlineStyle = element.getAttribute('style');
  if (inlineStyle) {
    const styleMap = parseInlineStyle(inlineStyle);
    value = styleMap[kebabCase] || styleMap[camelCase];
  }

  // 2. Check computed style
  if (!value) {
    value = styles[camelCase] || styles[kebabCase];
  }

  // 3. Check element attribute
  if (!value) {
    value = element.getAttribute(kebabCase) || element.getAttribute(camelCase);
  }

  return value || fallback;
}
export function resolveLineStrokeDasharray(element, _styles) {
  const value =
    element.getAttribute('stroke-dasharray') || getStyles(element).strokeDasharray || '';

  if (!value || value === 'none') {
    return ''; // Default dash pattern for lines as string
  }

  // Parse the stroke-dasharray string into an array of numbers, then back to string
  const values = value.trim().split(/[\s,]+/);
  const dashArray = [];

  for (const val of values) {
    const num = parseFloat(val);
    // Ensure values are valid positive numbers
    if (!isNaN(num) && num >= 0) {
      dashArray.push(num);
    }
  }

  // If we have valid dash values, return them as string; otherwise return default
  return dashArray.length > 0 ? dashArray.join(',') : '';
}

// Check for arrow markers with comprehensive pattern matching and fallback detection
export const detectArrowMarker = (element, key) => {
  const styles = getStyles(element);
  const styleKeys = {
    'marker-start': 'markerStart',
    'marker-end': 'markerEnd',
  };

  // Check CSS styles first for marker properties
  const styleMarker = styles[key] || styles[styleKeys[key]] || '';

  // Parse marker attributes from element or styles
  const markerAttr = element.getAttribute(key) || styleMarker || '';
  if (!markerAttr || markerAttr === 'none') return 'none';

  // Common arrow marker patterns - updated to handle both quoted and unquoted URLs
  const arrowPatterns = [
    /url\(['"]?#.*arrow.*['"]?\)/i,
    /url\(['"]?#.*head.*['"]?\)/i,
    /url\(['"]?#.*triangle.*['"]?\)/i,
    /url\(['"]?#.*marker.*['"]?\)/i,
    /url\(['"]?#.*pointer.*['"]?\)/i,
    /url\(['"]?#.*end.*['"]?\)/i,
    /url\(['"]?#.*start.*['"]?\)/i,
    /url\(['"]?#.*tip.*['"]?\)/i,
  ];

  // Check if any pattern matches
  if (arrowPatterns.some((pattern) => pattern.test(markerAttr))) {
    return 'arrow';
  }

  // Extract marker ID for further analysis
  const urlMatch = markerAttr.match(/url\(#([^)]+)\)/);
  if (urlMatch) {
    const markerId = urlMatch[1].toLowerCase();
    // Additional keyword checks
    const arrowKeywords = ['arrow', 'head', 'triangle', 'marker', 'pointer', 'tip', 'end', 'start'];
    if (arrowKeywords.some((keyword) => markerId.includes(keyword))) {
      return 'arrow';
    }
  }

  return 'none';
};

// Extract line coordinates if it's a simple line or polyline
export const extractLineCoords = (pathData) => {
  const normalized = pathData.trim().replace(/\s+/g, ' ');

  // Match pattern for basic line commands:
  // M x1 y1 L x2 y2 (MoveTo + LineTo)
  const moveLineTo =
    /^M\s*([+-]?\d*\.?\d+)[,\s]+([+-]?\d*\.?\d+)\s+L\s*([+-]?\d*\.?\d+)[,\s]+([+-]?\d*\.?\d+)\s*$/i;

  // M x1 y1 H x2 (MoveTo + Horizontal line) - more flexible spacing
  const moveHorizontal = /^M\s*([+-]?\d*\.?\d+)\s+([+-]?\d*\.?\d+)\s+H\s*([+-]?\d*\.?\d+)\s*$/i;

  // M x1 y1 V y2 (MoveTo + Vertical line) - more flexible spacing
  const moveVertical = /^M\s*([+-]?\d*\.?\d+)\s+([+-]?\d*\.?\d+)\s+V\s*([+-]?\d*\.?\d+)\s*$/i;

  // Complex path like "M575 170 V200 H250 V600" (MoveTo + multiple H/V commands)
  const multiSegment =
    /^M\s*([+-]?\d*\.?\d+)[,\s]+([+-]?\d*\.?\d+)(?:\s+[HV]\s*([+-]?\d*\.?\d+))+\s*$/i;

  // Try MoveTo + LineTo pattern (simple line)
  let match = normalized.match(moveLineTo);
  if (match) {
    return {
      x1: parseFloat(match[1]),
      y1: parseFloat(match[2]),
      x2: parseFloat(match[3]),
      y2: parseFloat(match[4]),
      isPolyline: false,
    };
  }

  // Try MoveTo + Horizontal line pattern (simple line)
  match = normalized.match(moveHorizontal);
  if (match) {
    return {
      x1: parseFloat(match[1]),
      y1: parseFloat(match[2]),
      x2: parseFloat(match[3]),
      y2: parseFloat(match[2]), // Same Y coordinate for horizontal line
      isPolyline: false,
    };
  }

  // Try MoveTo + Vertical line pattern (simple line)
  match = normalized.match(moveVertical);
  if (match) {
    return {
      x1: parseFloat(match[1]),
      y1: parseFloat(match[2]),
      x2: parseFloat(match[1]), // Same X coordinate for vertical line
      y2: parseFloat(match[3]),
      isPolyline: false,
    };
  }

  // Try multi-segment pattern (polyline)
  match = normalized.match(multiSegment);
  if (match) {
    const startX = parseFloat(match[1]);
    const startY = parseFloat(match[2]);
    let currentX = startX;
    let currentY = startY;
    const points = [{ x: currentX, y: currentY }];

    // Extract all H/V commands to build the complete polyline
    const hvCommands = normalized.match(/[HV]\s*([+-]?\d*\.?\d+)/gi);
    if (hvCommands && hvCommands.length > 0) {
      hvCommands.forEach((cmd) => {
        const value = parseFloat(cmd.substring(1).trim());
        if (cmd.toUpperCase().startsWith('H')) {
          currentX = value;
        } else if (cmd.toUpperCase().startsWith('V')) {
          currentY = value;
        }
        points.push({ x: currentX, y: currentY });
      });

      return {
        x1: startX,
        y1: startY,
        x2: currentX,
        y2: currentY,
        isPolyline: true,
        points: points,
      };
    }
  }

  return null;
};

// Handle global gradients and patterns
export const processGradients = (svgElement) => {
  const gradients = {};

  // Process linear gradients
  const linearGradients = svgElement.querySelectorAll('linearGradient');
  linearGradients.forEach((gradient) => {
    const id = gradient.getAttribute('id');
    if (!id) return;

    const x1 = gradient.getAttribute('x1') || '0%';
    const y1 = gradient.getAttribute('y1') || '0%';
    const x2 = gradient.getAttribute('x2') || '100%';
    const y2 = gradient.getAttribute('y2') || '0%';
    const gradientUnits = gradient.getAttribute('gradientUnits') || 'objectBoundingBox';
    const gradientTransform = gradient.getAttribute('gradientTransform') || '';

    const stops = [];
    gradient.querySelectorAll('stop').forEach((stop) => {
      // Get color from stop-color attribute or from style
      let color = stop.getAttribute('stop-color');
      let opacity = stop.getAttribute('stop-opacity');

      // Check if color and opacity are defined in style attribute
      const style = stop.getAttribute('style');
      if (style) {
        const styleMap = style.split(';').reduce((acc, item) => {
          const [key, value] = item.split(':').map((s) => s.trim());
          if (key && value) acc[key] = value;
          return acc;
        }, {});

        if (!color && styleMap['stop-color']) {
          color = styleMap['stop-color'];
        }

        if (!opacity && styleMap['stop-opacity']) {
          opacity = styleMap['stop-opacity'];
        }
      }

      // Convert color to rgba if opacity is specified
      if (color && opacity && opacity !== '1') {
        // Parse the color to get RGB values
        let r, g, b;
        if (color.startsWith('#')) {
          // Handle hex colors
          const hex = color.substring(1);
          if (hex.length === 3) {
            r = parseInt(hex[0] + hex[0], 16);
            g = parseInt(hex[1] + hex[1], 16);
            b = parseInt(hex[2] + hex[2], 16);
          } else {
            r = parseInt(hex.substring(0, 2), 16);
            g = parseInt(hex.substring(2, 4), 16);
            b = parseInt(hex.substring(4, 6), 16);
          }
          color = `rgba(${r}, ${g}, ${b}, ${opacity})`;
        } else if (color.startsWith('rgb(')) {
          // Handle rgb colors
          const rgb = color.match(/\d+/g).map(Number);
          color = `rgba(${rgb[0]}, ${rgb[1]}, ${rgb[2]}, ${opacity})`;
        } else if (color.startsWith('rgba(')) {
          // Already rgba, just update the alpha
          const rgba = color.match(/\d+(\.\d+)?/g).map(Number);
          color = `rgba(${rgba[0]}, ${rgba[1]}, ${rgba[2]}, ${opacity})`;
        }
      }

      stops.push({
        offset: stop.getAttribute('offset') || '0%',
        color: color || '#000',
        opacity: 1, // Opacity is now merged into the color value
      });
    });

    gradients[`url(#${id})`] = {
      type: 'linearGradient',
      x1,
      y1,
      x2,
      y2,
      gradientUnits,
      gradientTransform,
      stops,
    };
  });

  // Process radial gradients
  const radialGradients = svgElement.querySelectorAll('radialGradient');
  radialGradients.forEach((gradient) => {
    const id = gradient.getAttribute('id');
    if (!id) return;

    const cx = gradient.getAttribute('cx') || '50%';
    const cy = gradient.getAttribute('cy') || '50%';
    const r = gradient.getAttribute('r') || '50%';
    const fx = gradient.getAttribute('fx') || cx;
    const fy = gradient.getAttribute('fy') || cy;
    const gradientUnits = gradient.getAttribute('gradientUnits') || 'objectBoundingBox';
    const gradientTransform = gradient.getAttribute('gradientTransform') || '';

    const stops = [];
    gradient.querySelectorAll('stop').forEach((stop) => {
      // Get color from stop-color attribute or from style
      let color = stop.getAttribute('stop-color');
      let opacity = stop.getAttribute('stop-opacity');

      // Check if color and opacity are defined in style attribute
      const style = stop.getAttribute('style');
      if (style) {
        const styleMap = style.split(';').reduce((acc, item) => {
          const [key, value] = item.split(':').map((s) => s.trim());
          if (key && value) acc[key] = value;
          return acc;
        }, {});

        if (!color && styleMap['stop-color']) {
          color = styleMap['stop-color'];
        }

        if (!opacity && styleMap['stop-opacity']) {
          opacity = styleMap['stop-opacity'];
        }
      }

      // Convert color to rgba if opacity is specified
      if (color && opacity && opacity !== '1') {
        // Parse the color to get RGB values
        let r, g, b;
        if (color.startsWith('#')) {
          // Handle hex colors
          const hex = color.substring(1);
          if (hex.length === 3) {
            r = parseInt(hex[0] + hex[0], 16);
            g = parseInt(hex[1] + hex[1], 16);
            b = parseInt(hex[2] + hex[2], 16);
          } else {
            r = parseInt(hex.substring(0, 2), 16);
            g = parseInt(hex.substring(2, 4), 16);
            b = parseInt(hex.substring(4, 6), 16);
          }
          color = `rgba(${r}, ${g}, ${b}, ${opacity})`;
        } else if (color.startsWith('rgb(')) {
          // Handle rgb colors
          const rgb = color.match(/\d+/g).map(Number);
          color = `rgba(${rgb[0]}, ${rgb[1]}, ${rgb[2]}, ${opacity})`;
        } else if (color.startsWith('rgba(')) {
          // Already rgba, just update the alpha
          const rgba = color.match(/\d+(\.\d+)?/g).map(Number);
          color = `rgba(${rgba[0]}, ${rgba[1]}, ${rgba[2]}, ${opacity})`;
        }
      }

      stops.push({
        offset: stop.getAttribute('offset') || '0%',
        color: color || '#000',
        opacity: 1, // Opacity is now merged into the color value
      });
    });

    gradients[`url(#${id})`] = {
      type: 'radialGradient',
      cx,
      cy,
      r,
      fx,
      fy,
      gradientUnits,
      gradientTransform,
      stops,
    };
  });

  return gradients;
};

// Process patterns
export const processPatterns = (svgElement) => {
  const patterns = {};

  const patternElements = svgElement.querySelectorAll('pattern');
  patternElements.forEach((pattern) => {
    const id = pattern.getAttribute('id');
    if (!id) return;

    const width = pattern.getAttribute('width') || '100%';
    const height = pattern.getAttribute('height') || '100%';
    const patternUnits = pattern.getAttribute('patternUnits') || 'objectBoundingBox';
    const patternContentUnits = pattern.getAttribute('patternContentUnits') || 'userSpaceOnUse';
    const patternTransform = pattern.getAttribute('patternTransform') || '';

    patterns[`url(#${id})`] = {
      type: 'pattern',
      width,
      height,
      patternUnits,
      patternContentUnits,
      patternTransform,
      content: pattern.innerHTML,
    };
  });

  return patterns;
};

// Process filters in SVG
export const processFilters = (svgElement) => {
  const filters = {};

  const filterElements = svgElement.querySelectorAll('filter');
  filterElements.forEach((filter) => {
    const id = filter.getAttribute('id');
    if (!id) return;

    const x = filter.getAttribute('x') || '0%';
    const y = filter.getAttribute('y') || '0%';
    const width = filter.getAttribute('width') || '100%';
    const height = filter.getAttribute('height') || '100%';
    const filterUnits = filter.getAttribute('filterUnits') || 'objectBoundingBox';
    const primitiveUnits = filter.getAttribute('primitiveUnits') || 'userSpaceOnUse';

    // Process filter primitives
    const primitives = [];
    filter.querySelectorAll('*').forEach((primitive) => {
      const tagName = primitive.tagName.toLowerCase();
      if (tagName.startsWith('fe')) {
        const primitiveObj = {
          type: tagName,
          attributes: {},
        };

        // Extract all attributes
        Array.from(primitive.attributes).forEach((attr) => {
          primitiveObj.attributes[attr.name] = attr.value;
        });

        primitives.push(primitiveObj);
      }
    });

    filters[`url(#${id})`] = {
      type: 'filter',
      x,
      y,
      width,
      height,
      filterUnits,
      primitiveUnits,
      primitives,
      content: filter.innerHTML,
    };
  });

  return filters;
};

export const filterToRenderStr = (filterDef) => {
  if (!filterDef || !filterDef.primitives) return '';

  const filterEffects = [];

  // Process each filter primitive
  filterDef.primitives.forEach((primitive) => {
    if (!primitive.type) return;

    const type = primitive.type.toLowerCase();
    const attrs = primitive.attributes || {};

    switch (type) {
      case 'fegaussianblur':
        filterEffects.push({
          type: 'blur',
          in: attrs.in || 'SourceGraphic',
          stdDeviation: parseFloat(attrs.stdDeviation) || 0,
          result: attrs.result || undefined,
        });
        break;

      case 'fecolormatrix':
        if (attrs.type === 'saturate') {
          filterEffects.push({
            type: 'saturate',
            in: attrs.in || 'SourceGraphic',
            value: parseFloat(attrs.values) || 1,
            result: attrs.result || undefined,
          });
        } else if (attrs.type === 'hueRotate') {
          filterEffects.push({
            type: 'hueRotate',
            in: attrs.in || 'SourceGraphic',
            angle: parseFloat(attrs.values) || 0,
            result: attrs.result || undefined,
          });
        } else if (attrs.type === 'matrix') {
          filterEffects.push({
            type: 'colorMatrix',
            in: attrs.in || 'SourceGraphic',
            matrix: attrs.values ? attrs.values.split(/\s+/).map(parseFloat) : [],
            result: attrs.result || undefined,
          });
        }
        break;

      case 'fecomponenttransfer':
        // Handle brightness, contrast, etc.
        if (primitive.children) {
          const funcR = primitive.children.find((c) => c.type === 'fefuncr');

          if (funcR && funcR.attributes?.type === 'linear') {
            filterEffects.push({
              type: 'brightness',
              in: attrs.in || 'SourceGraphic',
              value: parseFloat(funcR.attributes.slope) || 1,
              result: attrs.result || undefined,
            });
          }
        }
        break;

      case 'fedropshadow':
        filterEffects.push({
          type: 'dropShadow',
          in: attrs.in || 'SourceGraphic',
          dx: parseFloat(attrs.dx) || 0,
          dy: parseFloat(attrs.dy) || 0,
          stdDeviation: parseFloat(attrs.stdDeviation) || 0,
          floodColor: attrs['flood-color'] || 'rgba(0,0,0,1)',
          floodOpacity: parseFloat(attrs['flood-opacity']) || 1,
          result: attrs.result || undefined,
        });
        break;

      case 'feblend':
        filterEffects.push({
          type: 'blend',
          in: attrs.in || 'SourceGraphic',
          in2: attrs.in2 || 'SourceGraphic',
          mode: attrs.mode || 'normal',
          result: attrs.result || undefined,
        });
        break;

      case 'feoffset':
        filterEffects.push({
          type: 'offset',
          in: attrs.in || 'SourceGraphic',
          dx: parseFloat(attrs.dx) || 0,
          dy: parseFloat(attrs.dy) || 0,
          result: attrs.result || undefined,
        });
        break;

      case 'fecomposite':
        filterEffects.push({
          type: 'composite',
          in: attrs.in || 'SourceGraphic',
          in2: attrs.in2 || 'SourceGraphic',
          operator: attrs.operator || 'over',
          result: attrs.result || undefined,
        });
        break;

      case 'femerge': {
        const mergeNodes = [];
        if (primitive.children) {
          primitive.children.forEach((node) => {
            if (node.type === 'femergenode' && node.attributes?.in) {
              mergeNodes.push(node.attributes.in);
            }
          });
        }

        filterEffects.push({
          type: 'merge',
          inputs: mergeNodes.length > 0 ? mergeNodes : ['SourceGraphic'],
          result: attrs.result || undefined,
        });
        break;
      }
    }
  });

  return `seede-svg-filter(${JSON.stringify(filterEffects)})`;
};

// Helper function to convert transformation matrix to string
export function matrixToString(matrix) {
  if (!matrix || matrix.length !== 6) return '';

  // Check if it's an identity matrix
  const [a, b, c, d, e, f] = matrix;
  const isIdentity = a === 1 && b === 0 && c === 0 && d === 1 && e === 0 && f === 0;

  if (isIdentity) return '';

  // Format matrix values to reasonable precision
  const formatValue = (value) => {
    const rounded = Math.round(value * 100000) / 100000;
    return rounded === Math.floor(rounded) ? Math.floor(rounded) : rounded;
  };

  const formattedValues = matrix.map(formatValue);
  return `${formattedValues.join(',')}`;
}

// Helper function to multiply transformation matrices
export function multiplyMatrix(a, b) {
  return [
    a[0] * b[0] + a[1] * b[2],
    a[0] * b[1] + a[1] * b[3],
    a[2] * b[0] + a[3] * b[2],
    a[2] * b[1] + a[3] * b[3],
    a[0] * b[4] + a[1] * b[5] + a[4],
    a[2] * b[4] + a[3] * b[5] + a[5],
  ];
}

// Process images in SVG
export const processImages = (svgElement) => {
  const images = {};

  const imageElements = svgElement.querySelectorAll('image');
  imageElements.forEach((image) => {
    const id = image.getAttribute('id');
    if (!id) return;

    const x = image.getAttribute('x') || '0';
    const y = image.getAttribute('y') || '0';
    const width = image.getAttribute('width') || '0';
    const height = image.getAttribute('height') || '0';
    const href = image.getAttribute('href') || image.getAttribute('xlink:href') || '';
    const preserveAspectRatio = image.getAttribute('preserveAspectRatio') || 'xMidYMid meet';
    const opacity = parseFloat(image.getAttribute('opacity') || '1');
    const transform = image.getAttribute('transform') || '';

    images[`url(#${id})`] = {
      type: 'image',
      x,
      y,
      width,
      height,
      href,
      preserveAspectRatio,
      opacity,
      transform,
    };
  });

  return images;
};

export const isImageFilled = (element, defs) => {
  const fill = getStyleValue(element, 'fill', 'transparent');

  const { images, patterns } = defs;
  // Check if fill is a URL reference to an image
  if (fill.startsWith('url(#')) {
    const imageRef = images[fill];

    // Return the image reference if it exists
    if (imageRef && imageRef.type === 'image' && imageRef.href) {
      return imageRef;
    }
  }

  // Also check for pattern fills that might contain images
  if (fill.startsWith('url(#') && patterns[fill]) {
    const pattern = patterns[fill];
    // Check if pattern contains an image
    if (pattern.content && pattern.content.includes('<image')) {
      return {
        href: pattern.content.match(/href=["']([^"']+)["']/)?.[1] || '',
        width: parseFloat(pattern.width) || 0,
        height: parseFloat(pattern.height) || 0,
        preserveAspectRatio: 'xMidYMid meet',
        opacity: 1,
      };
    }

    // Check for pattern with xlink:href to image
    if (pattern.content.includes('#image')) {
      // <use xlink:href="#image1_4058_693" transform="scale(0.00208333)"></use>
      // Check if pattern contains a use element with xlink:href
      if (pattern.content && pattern.content.includes('xlink:href="#image')) {
        // Extract the image ID from the xlink:href attribute
        const imageId = pattern.content.match(/xlink:href=["']#([^"']+)["']/)?.[1];

        if (imageId && images[`url(#${imageId})`]) {
          const imageRef = images[`url(#${imageId})`];

          // Extract transform matrix if available
          const transformMatch = pattern.content.match(/transform=["']matrix\(([^"']+)\)["']/);
          let transform = '';

          if (transformMatch && transformMatch[1]) {
            transform = `${transformMatch[1]}`;
          }

          return {
            href: imageRef.href,
            width: parseFloat(pattern.width) || imageRef.width || 0,
            height: parseFloat(pattern.height) || imageRef.height || 0,
            preserveAspectRatio: imageRef.preserveAspectRatio || 'xMidYMid meet',
            opacity: imageRef.opacity || 1,
            transform: transform,
          };
        }
      }
    }
  }

  return false;
};

// Check if path is a simple line (2 points) or polyline
export const isSimpleLine = (pathData) => {
  // Remove whitespace and normalize
  const normalized = pathData.trim().replace(/\s+/g, ' ');

  // Match pattern for basic line commands:
  // M x1 y1 L x2 y2 (MoveTo + LineTo) - simple 2-point line
  const moveLineTo =
    /^M\s*([+-]?\d*\.?\d+)[,\s]+([+-]?\d*\.?\d+)\s+L\s*([+-]?\d*\.?\d+)[,\s]+([+-]?\d*\.?\d+)\s*$/i;

  // M x1 y1 H x2 (MoveTo + Horizontal line) - simple horizontal line
  const moveHorizontal = /^M\s*([+-]?\d*\.?\d+)\s+([+-]?\d*\.?\d+)\s+H\s*([+-]?\d*\.?\d+)\s*$/i;

  // M x1 y1 V y2 (MoveTo + Vertical line) - simple vertical line
  const moveVertical = /^M\s*([+-]?\d*\.?\d+)\s+([+-]?\d*\.?\d+)\s+V\s*([+-]?\d*\.?\d+)\s*$/i;

  // Complex path like "M575 170 V200 H250 V600" (MoveTo + multiple H/V commands) - polyline
  const multiSegment =
    /^M\s*([+-]?\d*\.?\d+)[,\s]+([+-]?\d*\.?\d+)(?:\s+[HV]\s*([+-]?\d*\.?\d+))+\s*$/i;

  return (
    moveLineTo.test(normalized) ||
    moveHorizontal.test(normalized) ||
    moveVertical.test(normalized) ||
    multiSegment.test(normalized)
  );
};

// Helper function to convert SVG filter to CSS filter
export const filterToCSS = (filterDef) => {
  if (!filterDef || !filterDef.primitives) return '';

  const cssFilters = [];

  // Process each filter primitive
  filterDef.primitives.forEach((primitive) => {
    if (!primitive.type) return;

    switch (primitive.type) {
      case 'fegaussianblur': {
        const stdDeviation = primitive.attributes?.stdDeviation || 0;
        cssFilters.push(`blur(${stdDeviation}px)`);
        break;
      }
      case 'fecolormatrix':
        if (primitive.attributes?.type === 'saturate') {
          const value = primitive.attributes?.values || 1;
          cssFilters.push(`saturate(${value})`);
        } else if (primitive.attributes?.type === 'hueRotate') {
          const value = primitive.attributes?.values || 0;
          cssFilters.push(`hue-rotate(${value}deg)`);
        } else if (primitive.attributes?.type === 'luminanceToAlpha') {
          // No direct CSS equivalent, approximate with opacity
          cssFilters.push('opacity(0.5)');
        }
        break;

      case 'fecomponenttransfer':
        // Handle brightness and contrast
        if (primitive.children) {
          const hasBrightness = primitive.children.some(
            (c) => c.type === 'fefuncr' && c.attributes?.type === 'linear' && c.attributes?.slope
          );

          if (hasBrightness) {
            const slope =
              primitive.children.find((c) => c.type === 'fefuncr')?.attributes?.slope || 1;
            cssFilters.push(`brightness(${slope})`);
          }
        }
        break;

      case 'fedropshadow': {
        const dx = primitive.attributes?.dx || 0;
        const dy = primitive.attributes?.dy || 0;
        const stdDev = primitive.attributes?.stdDeviation || 0;
        const floodOpacity = primitive.attributes?.['flood-opacity'] || 1;
        cssFilters.push(`drop-shadow(${dx}px ${dy}px ${stdDev}px rgba(0,0,0,${floodOpacity}))`);
        break;
      }
      case 'feblend':
        // No direct equivalent in CSS filters
        break;

      case 'feoffset':
        // Handled as part of drop-shadow if present
        break;
    }
  });

  return cssFilters.length > 0 ? cssFilters.join(' ') : '';
};
